import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Upload, Download, AlertCircle, LayoutGrid, RotateCcw, Image as ImageIcon, DownloadCloud } from 'lucide-react';

interface GridOption {
  label: string;
  cols: number;
  rows: number;
}

const GRID_OPTIONS: GridOption[] = [
  { label: '1x2', cols: 1, rows: 2 },
  { label: '1x3', cols: 1, rows: 3 },
  { label: '2x2', cols: 2, rows: 2 },
  { label: '3x1', cols: 3, rows: 1 },
  { label: '3x3', cols: 3, rows: 3 },
  { label: '2x3', cols: 2, rows: 3 },
  { label: '3x2', cols: 3, rows: 2 },
];

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const ImageSplitter: React.FC = () => {
  const [file, setFile] = useState<File | null>(null);
  const [originalPreview, setOriginalPreview] = useState<string | null>(null);
  const [grid, setGrid] = useState<GridOption>(GRID_OPTIONS[4]); // default 3x3
  const [error, setError] = useState<string | null>(null);
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ w: number; h: number } | null>(null);
  const [splitPreviews, setSplitPreviews] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const previewCanvasRef = useRef<HTMLCanvasElement>(null);

  const handleFileSelect = useCallback((selectedFiles: FileList | null) => {
    if (!selectedFiles || selectedFiles.length === 0) return;
    const f = selectedFiles[0];
    if (!f.type.startsWith('image/')) {
      setError('Please select a valid image file.');
      return;
    }
    setFile(f);
    setError(null);
    setSplitPreviews([]);
    const reader = new FileReader();
    reader.onload = (e) => {
      const url = e.target?.result as string;
      setOriginalPreview(url);
      const img = new window.Image();
      img.onload = () => {
        setImgElement(img);
        setImageDimensions({ w: img.naturalWidth, h: img.naturalHeight });
      };
      img.onerror = () => setError('Failed to load image.');
      img.src = url;
    };
    reader.readAsDataURL(f);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
  }, []);

  // Draw grid overlay on preview
  const drawGridPreview = useCallback((canvas: HTMLCanvasElement, img: HTMLImageElement) => {
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const w = img.naturalWidth;
    const h = img.naturalHeight;
    canvas.width = w;
    canvas.height = h;

    ctx.drawImage(img, 0, 0);

    // Draw grid lines
    const cellW = w / grid.cols;
    const cellH = h / grid.rows;

    ctx.strokeStyle = 'rgba(255, 255, 255, 0.8)';
    ctx.lineWidth = Math.max(2, Math.min(w, h) / 200);
    ctx.setLineDash([10, 5]);

    // Vertical lines
    for (let c = 1; c < grid.cols; c++) {
      ctx.beginPath();
      ctx.moveTo(c * cellW, 0);
      ctx.lineTo(c * cellW, h);
      ctx.stroke();
    }

    // Horizontal lines
    for (let r = 1; r < grid.rows; r++) {
      ctx.beginPath();
      ctx.moveTo(0, r * cellH);
      ctx.lineTo(w, r * cellH);
      ctx.stroke();
    }

    ctx.setLineDash([]);

    // Draw numbers
    const fontSize = Math.max(14, Math.min(cellW, cellH) / 4);
    ctx.font = `bold ${fontSize}px Arial`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    let num = 1;
    for (let r = 0; r < grid.rows; r++) {
      for (let c = 0; c < grid.cols; c++) {
        const cx = c * cellW + cellW / 2;
        const cy = r * cellH + cellH / 2;
        // Background circle
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
        ctx.beginPath();
        ctx.arc(cx, cy, fontSize * 0.8, 0, Math.PI * 2);
        ctx.fill();
        // Number text
        ctx.fillStyle = '#ffffff';
        ctx.fillText(String(num), cx, cy);
        num++;
      }
    }
  }, [grid]);

  // Generate split images
  const generateSplits = useCallback((img: HTMLImageElement): string[] => {
    const w = img.naturalWidth;
    const h = img.naturalHeight;
    const cellW = Math.floor(w / grid.cols);
    const cellH = Math.floor(h / grid.rows);
    const previews: string[] = [];

    for (let r = 0; r < grid.rows; r++) {
      for (let c = 0; c < grid.cols; c++) {
        const canvas = document.createElement('canvas');
        canvas.width = cellW;
        canvas.height = cellH;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        ctx.drawImage(img, c * cellW, r * cellH, cellW, cellH, 0, 0, cellW, cellH);
        previews.push(canvas.toDataURL('image/png'));
      }
    }

    return previews;
  }, [grid]);

  useEffect(() => {
    if (!imgElement) return;
    if (previewCanvasRef.current) {
      drawGridPreview(previewCanvasRef.current, imgElement);
    }
    const previews = generateSplits(imgElement);
    setSplitPreviews(previews);
  }, [imgElement, drawGridPreview, generateSplits]);

  const downloadSingle = useCallback((dataUrl: string, index: number) => {
    const link = document.createElement('a');
    const baseName = file?.name?.replace(/\.[^.]+$/, '') || 'split';
    link.download = `${baseName}-${index + 1}.png`;
    link.href = dataUrl;
    link.click();
  }, [file]);

  const downloadAll = useCallback(() => {
    splitPreviews.forEach((dataUrl, index) => {
      setTimeout(() => {
        const link = document.createElement('a');
        const baseName = file?.name?.replace(/\.[^.]+$/, '') || 'split';
        link.download = `${baseName}-${index + 1}.png`;
        link.href = dataUrl;
        link.click();
      }, index * 200); // Stagger downloads
    });
  }, [splitPreviews, file]);

  const handleReset = useCallback(() => {
    setFile(null);
    setOriginalPreview(null);
    setImgElement(null);
    setImageDimensions(null);
    setSplitPreviews([]);
    setGrid(GRID_OPTIONS[4]);
    setError(null);
  }, []);

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6">
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <LayoutGrid className="w-6 h-6 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Image Splitter</h1>
        </div>
        <p className="text-gray-600">Split images into grid pieces — perfect for Instagram carousel posts.</p>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <span className="text-sm">{error}</span>
        </div>
      )}

      {!file ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onClick={() => fileInputRef.current?.click()}
          className="border-2 border-dashed border-gray-300 rounded-xl p-12 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
        >
          <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-gray-700 mb-1">Drop an image here or click to upload</p>
          <p className="text-sm text-gray-500">Supports PNG, JPG, WebP, GIF</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => handleFileSelect(e.target.files)}
          />
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white border border-gray-200 rounded-xl p-4 sm:p-6 space-y-5">
            <div className="flex items-center justify-between">
              <h2 className="font-semibold text-gray-900">Grid Settings</h2>
              <button onClick={handleReset} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
                <RotateCcw className="w-4 h-4" /> Reset
              </button>
            </div>

            {/* Grid selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Grid Layout (columns x rows)</label>
              <div className="flex flex-wrap gap-2">
                {GRID_OPTIONS.map((opt) => (
                  <button
                    key={opt.label}
                    onClick={() => setGrid(opt)}
                    className={`relative p-3 rounded-lg border transition-colors min-w-[80px] ${
                      grid.label === opt.label
                        ? 'border-blue-500 bg-blue-50 text-blue-700'
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                    }`}
                  >
                    {/* Mini grid visualization */}
                    <div
                      className="mx-auto mb-1 border border-current rounded-sm overflow-hidden"
                      style={{
                        width: 40,
                        height: 40,
                        display: 'grid',
                        gridTemplateColumns: `repeat(${opt.cols}, 1fr)`,
                        gridTemplateRows: `repeat(${opt.rows}, 1fr)`,
                        gap: 1,
                      }}
                    >
                      {Array.from({ length: opt.cols * opt.rows }).map((_, i) => (
                        <div key={i} className={`${grid.label === opt.label ? 'bg-blue-200' : 'bg-gray-200'}`} />
                      ))}
                    </div>
                    <div className="text-xs font-medium text-center">{opt.label}</div>
                    <div className="text-xs text-gray-500 text-center">{opt.cols * opt.rows} pieces</div>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Image info */}
          {imageDimensions && file && (
            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
              <span className="flex items-center gap-1"><ImageIcon className="w-4 h-4" /> {imageDimensions.w} x {imageDimensions.h}px</span>
              <span>{formatFileSize(file.size)}</span>
              <span>Each piece: {Math.floor(imageDimensions.w / grid.cols)} x {Math.floor(imageDimensions.h / grid.rows)}px</span>
            </div>
          )}

          {/* Grid preview */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-2">Grid Preview</h3>
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
              <canvas ref={previewCanvasRef} className="w-full h-auto" />
            </div>
          </div>

          {/* Split pieces preview */}
          {splitPreviews.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-gray-700">Split Pieces ({splitPreviews.length} total)</h3>
                <button
                  onClick={downloadAll}
                  className="flex items-center gap-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                >
                  <DownloadCloud className="w-4 h-4" /> Download All
                </button>
              </div>
              <div
                className="grid gap-3"
                style={{ gridTemplateColumns: `repeat(${grid.cols}, 1fr)` }}
              >
                {splitPreviews.map((preview, index) => (
                  <div key={index} className="relative group">
                    <div className="border border-gray-200 rounded-lg overflow-hidden bg-gray-50">
                      <img src={preview} alt={`Piece ${index + 1}`} className="w-full h-auto" />
                    </div>
                    {/* Number badge */}
                    <div className="absolute top-2 left-2 w-6 h-6 bg-black/60 text-white text-xs font-bold rounded-full flex items-center justify-center">
                      {index + 1}
                    </div>
                    {/* Download button overlay */}
                    <button
                      onClick={() => downloadSingle(preview, index)}
                      className="absolute top-2 right-2 w-7 h-7 bg-white/90 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm hover:bg-white"
                      title={`Download piece ${index + 1}`}
                    >
                      <Download className="w-3.5 h-3.5 text-gray-700" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Download all button */}
          <div className="flex gap-3">
            <button
              onClick={downloadAll}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              <DownloadCloud className="w-5 h-5" /> Download All {splitPreviews.length} Pieces
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ImageSplitter;
